/**
 * Scene/shot numbering utilities — mirrors the Unity numbering system
 * (SceneAndShotNumberingManager + StringUtilities.GetAlphabeticalShotNumberFromShotIndex).
 *
 * Used in standalone mode (previsLoader) and as a fallback in loadProject (actions.ts).
 */

/** Reduced 23-letter set — skips I, O, S (ambiguous characters). */
const REDUCED = 'ABCDEFGHJKLMNPQRTUVWXYZ';

/** Full A-Z alphabet. */
const FULL = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

/**
 * Convert a 0-based index to a shot label.
 *
 * - If `useDigits`: returns "1", "2", "3", ...
 * - If letters: bijective base-N numeration (A..Z, AA, AB, ...) using the chosen character set.
 *   Matches C# `StringUtilities.GetAlphabeticalShotNumberFromShotIndex`.
 */
export function shotIndexToLabel(index: number, useFullAlphabet: boolean, useDigits: boolean): string {
  if (useDigits) return (index + 1).toString();

  const chars = useFullAlphabet ? FULL : REDUCED;
  const base = chars.length;
  let value = index + 1; // 1-based bijective
  let result = '';

  do {
    value--;
    result = chars[value % base] + result;
    value = Math.floor(value / base);
  } while (value > 0);

  return result;
}

/**
 * Compute scene numbers for an ordered list of scenes.
 *
 * Scenes with a non-empty `number` field use it as-is (manual override) and
 * don't increment the auto counter. Scenes with empty `number` get the next
 * auto-number starting from `startingNumber`.
 */
export function computeSceneNumbers(
  scenes: ReadonlyArray<{ number: string }>,
  startingNumber: number,
): string[] {
  let counter = startingNumber;
  return scenes.map(scene => {
    if (scene.number && scene.number.trim() !== '') {
      return scene.number;
    }
    return (counter++).toString();
  });
}

/**
 * Compute shot numbers for an ordered list of shots within a scene.
 *
 * Mirrors Unity's SceneAndShotNumberingManager:
 * - Empty `number` → compute from position counter using document settings
 * - Non-empty `number` → manual override, use as-is, do NOT increment counter
 */
export function computeShotNumbers(
  shots: ReadonlyArray<{ number: string }>,
  useFullAlphabet: boolean,
  useDigits: boolean,
): string[] {
  let counter = 0;
  return shots.map(shot => {
    if (shot.number && shot.number.trim() !== '') {
      return shot.number;
    }
    return shotIndexToLabel(counter++, useFullAlphabet, useDigits);
  });
}
